// 先代の姫の「追う照明」で指折りを知らせる、戦っている場面の影絵（2026-11-05・駅0⁗ の続き）。
//   作者（11-05 best=c＝追う照明）「見やすさとか的には、O証明（＝追う照明）が良さそうなんだけど。なんかあんまり戦闘のイメージがわからない
//   というか。……映像っぽいもので見ないとわかんないな。」
//   一番の案と「見やすい」と言った案は同じ追う照明だったので、比べる2本は ①追う照明だけ ②追う照明＋床に残る光の跡
//   （前のページで AI が「数をはっきりさせたければ組み合わせる」と書いた形）にした。
//   場面は2周：1周目は主人公が輪の中に残って大技を受ける、2周目は白く固まった輪から跳んで出て、空振りの戻りに斬り返す。
//   装置の追う照明（11-05 device=spot）の「光の輪が主人公を追い、止まって白く強まった所へ大技・輪から出れば外れる」を、
//   指折りの合図と同じ輪で兼ねる。拍は仮：合図から大技までを前の 300ms から 800ms に延ばした（輪が追って止まり、出る間を見せるため）。

import { circ, poly } from '../body'
import { bakeTracks, trackAt, type MockupMove, type MockupPhase, type StageEnv, type Tracks, type Vec } from '../core'
import { armTracks, bodyTracks, build } from './princess_count_tells'

type Ctx = CanvasRenderingContext2D
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const ramp = (t: number, a: number, b: number) => clamp01((t - a) / (b - a))
const smooth = (u: number) => u * u * (3 - 2 * u)

/** 1周の拍（仮）。lock＝輪が止まって白く固まる時刻 */
export const PRINCESS_BATTLE_BEAT = { j1: 660, j2: 1260, tell: 1600, lock: 2050, lunge: 2400, round: 3500 }
const B = PRINCESS_BATTLE_BEAT
const ROUND = B.round
const tracks: Tracks = { ...bodyTracks(B), ...armTracks([[0, 'hip'], [B.tell + 120, 'show'], [B.lunge - 80, 'show'], [B.lunge + 80, 'back'], [B.lunge + 400, 'back'], [B.lunge + 800, 'hip']]) }
const KEYS = bakeTracks(tracks, ROUND, build)

/** 剣先（模型の空間・rx 込み） */
function tipAt(t: number): Vec {
  const v: Record<string, number | Vec> = {}
  for (const k in tracks) v[k] = trackAt(tracks[k]!, t)
  const p = build(v), h = p.hF as Vec, a = (p.sF as number) * Math.PI / 180
  return [h[0] + Math.cos(a) * 64 + (p.rx as number), h[1] + Math.sin(a) * 64]
}

// ---------- 主人公（幅 400 の画面の x）----------
const HERO0 = 157
type HeroPose = 'stand' | 'parry' | 'hit' | 'hop' | 'run' | 'slash'
interface Hero { x: number; pose: HeroPose; lean: number; lift: number }
/** 2周目の動き：跳んで輪から出る→空振りの戻りへ走り込んで斬る→下がる */
const WALK = 16, OUT_X = HERO0 - 76, IN_X = 222
export function heroAt(t: number): Hero {
  const r = Math.floor(t / ROUND), u = t - r * ROUND
  const parry = [B.j1, B.j2].some(h => u >= h - 70 && u < h + 160)
  if (r === 0) {
    // 1周目：輪の中に残って大技を受け、飛ばされて戻る
    const knock = u >= B.lunge ? 1 - smooth(ramp(u, B.lunge + 450, B.lunge + 1050)) : 0
    const k1 = u >= B.lunge ? smooth(ramp(u, B.lunge, B.lunge + 140)) : 0
    const x = HERO0 - 34 * Math.min(k1, knock)
    if (u >= B.lunge && u < B.lunge + 450) return { x, pose: 'hit', lean: -24 * (1 - ramp(u, B.lunge + 250, B.lunge + 450)), lift: 0 }
    return { x, pose: parry ? 'parry' : 'stand', lean: 0, lift: 0 }
  }
  // 2周目：合図で少し下がる（輪が追ってくる）→止まった輪から跳んで出る
  const walk = smooth(ramp(u, B.tell + 80, B.lock - 60))
  const hop = smooth(ramp(u, B.lock + 60, B.lock + 260))
  const run = smooth(ramp(u, B.lunge + 260, B.lunge + 480))
  const back = smooth(ramp(u, B.lunge + 760, B.round - 40))
  const x = HERO0 - WALK * walk - (HERO0 - WALK - OUT_X) * hop + (IN_X - OUT_X) * run - (IN_X - HERO0) * back
  if (u >= B.lock + 60 && u < B.lock + 260) return { x, pose: 'hop', lean: -8, lift: Math.sin(Math.PI * ramp(u, B.lock + 60, B.lock + 260)) * 16 }
  if (u >= B.tell + 80 && u < B.lock - 60) return { x, pose: 'stand', lean: -4, lift: 0 }
  if (u >= B.lunge + 260 && u < B.lunge + 480) return { x, pose: 'run', lean: 14, lift: 0 }
  if (u >= B.lunge + 480 && u < B.lunge + 680) return { x, pose: 'slash', lean: 10 * (1 - ramp(u, B.lunge + 480, B.lunge + 680)), lift: 0 }
  return { x, pose: parry ? 'parry' : 'stand', lean: 0, lift: 0 }
}
/** 主人公の斬り返しが当たる時刻（2周目の中の時刻） */
export const HERO_SLASH_AT = B.lunge + 540

function drawHeroFig(ctx: Ctx, t: number, e: StageEnv): void {
  const { k, G } = e, h = heroAt(t), s = 0.62 * k, x = h.x * k, g = G - h.lift * k
  const a = h.lean * Math.PI / 180
  // 腰を軸に上体を傾ける（＋で前＝右へ）
  const up = (dy: number, dx = 0): Vec => [x + dx * Math.cos(a) + dy * Math.sin(a), g - 52 * s - dy * Math.cos(a) + dx * Math.sin(a)]
  ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.8 * k; ctx.lineCap = 'round'; ctx.globalAlpha = 0.9
  const seg = (p: Vec, q: Vec) => { ctx.beginPath(); ctx.moveTo(p[0], p[1]); ctx.lineTo(q[0], q[1]); ctx.stroke() }
  const hip: Vec = [x, g - 52 * s], neck = up(44 * s), head = up(56 * s)
  ctx.beginPath(); ctx.arc(head[0], head[1], 12.6 * s, 0, Math.PI * 2); ctx.stroke()
  seg(hip, neck)
  const wide = h.pose === 'run' || h.pose === 'slash' ? 10 : h.pose === 'hop' ? 4 : 6
  seg(hip, [x - wide * k, g]); seg(hip, [x + wide * k, g])
  const sh = up(36 * s)
  // 前の腕と剣：構え（斜め上）・パリィ（前へ立てる）・斬り（振り抜き）
  const arm: Record<HeroPose, [Vec, number]> = {
    stand: [[12, 22], -40], parry: [[16, 34], -85], hit: [[6, 30], -120], hop: [[10, 24], -30], run: [[12, 18], 10], slash: [[16, 20], 30],
  }
  const [[ax, ay], sw] = arm[h.pose], hand: Vec = [sh[0] + ax * s, sh[1] + (ay - 36) * s * 0.6]
  seg(sh, hand); seg(sh, [sh[0] - 8 * s, sh[1] + 22 * s])
  const sa = sw * Math.PI / 180
  ctx.strokeStyle = '#d8dde6'; ctx.lineWidth = 1.4 * k
  seg(hand, [hand[0] + Math.cos(sa) * 34 * s, hand[1] + Math.sin(sa) * 34 * s])
  ctx.globalAlpha = 1
}

/** パリィの火花と、斬り返し・大技が当たった時の閃き */
function drawSparks(ctx: Ctx, t: number, e: StageEnv): void {
  const { k } = e, r = Math.floor(t / ROUND), u = t - r * ROUND
  for (const hAt of [B.j1, B.j2]) {
    const d = u - hAt
    if (d < 0 || d > 180) continue
    const tip = e.at(tipAt(hAt)), q: Vec = [Math.max(tip[0], (heroAt(t).x + 14) * k), tip[1]], v = 1 - d / 180
    ctx.strokeStyle = '#fff4d0'; ctx.lineWidth = 1.6 * k; ctx.globalAlpha = v
    for (let i = 0; i < 6; i++) {
      const a = i / 6 * Math.PI * 2 + 0.4, l = (6 + d / 10) * k
      ctx.beginPath(); ctx.moveTo(q[0] + Math.cos(a) * 3 * k, q[1] + Math.sin(a) * 3 * k); ctx.lineTo(q[0] + Math.cos(a) * l, q[1] + Math.sin(a) * l); ctx.stroke()
    }
    circ(ctx, q, 3 * k * v + 1, '#ffffff'); ctx.globalAlpha = 1
  }
  if (r === 0) {
    const d = u - B.lunge
    if (d >= 0 && d < 220) { ctx.globalAlpha = 0.22 * (1 - d / 220); ctx.fillStyle = '#ef5b63'; ctx.fillRect(0, 0, e.w, e.h); ctx.globalAlpha = 1 }
  } else {
    const d = u - HERO_SLASH_AT
    if (d >= -60 && d < 200) {
      const hx = heroAt(t).x * k, y = e.G - 60 * k, v = d < 0 ? 1 : 1 - d / 200
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 2.4 * k; ctx.globalAlpha = 0.85 * v
      ctx.beginPath(); ctx.arc(hx + 6 * k, y + 8 * k, 30 * k, -1.3, 0.9); ctx.stroke(); ctx.globalAlpha = 1
    }
  }
}

// ---------- 追う照明 ----------
const HER_X = 300
const SPOT_W = [62, 50, 38], LOCK_W = 30
/** 光の輪：中心の x・半幅・白さ・明るさ。合図で主人公へ移り、主人公を追い、lock で止まって白く固まる */
export function battleSpot(t: number): { x: number; w: number; white: number; a: number } {
  const u = t % ROUND
  let w = SPOT_W[0]!
  ;[B.j1 + 120, B.j2 + 120].forEach((at, i) => { w += (SPOT_W[i + 1]! - SPOT_W[i]!) * smooth(ramp(u, at, at + 90)) })
  const flick = [B.j1 + 120, B.j2 + 120].some(at => u >= at && u < at + 90) ? 0.7 : 1
  const go = smooth(ramp(u, B.tell, B.tell + 260)), ret = smooth(ramp(u, B.lunge + 600, B.lunge + 1000))
  // 追う：主人公の 140ms 前の位置へ寄る。止まった後は止まった時の位置のまま
  const chaseT = Math.min(u, B.lock) - 140 + (t - u)
  const target = heroAt(Math.max(t - u, chaseT)).x
  const x = HER_X + (target - HER_X) * go * (1 - ret)
  w = w + (LOCK_W + 8 - w) * go
  const lk = smooth(ramp(u, B.lock, B.lock + 90))
  w = w - 8 * lk * (1 - ret)
  w = w + (SPOT_W[0]! - w) * ret
  const white = lk * (1 - ret)
  // 止まる直前、2度だけ瞬く
  const blink = u > B.lock - 200 && u < B.lock && Math.floor((u - (B.lock - 200)) / 50) % 2 === 1 ? 0.6 : 1
  return { x, w, white, a: flick * blink * (1 + 0.5 * white) }
}
function drawSpot(ctx: Ctx, t: number, e: StageEnv): void {
  const { k, G } = e, sp = battleSpot(t), x = sp.x * k, w = sp.w * k, sx = 352 * k
  for (const [col, a] of [['#ffe6b0', 1 - sp.white], ['#ffffff', sp.white]] as const) {
    if (a <= 0.01) continue
    ctx.globalAlpha = 0.09 * a * sp.a; poly(ctx, [[sx - 5 * k, -4 * k], [sx + 5 * k, -4 * k], [x + w, G], [x - w, G]], col)
    for (const [rr, aa] of [[1, 0.22], [0.62, 0.22]] as const) {
      ctx.globalAlpha = aa * a * sp.a; ctx.fillStyle = col
      ctx.beginPath(); ctx.ellipse(x, G, w * rr, 7 * k * rr, 0, 0, Math.PI * 2); ctx.fill()
    }
  }
  ctx.globalAlpha = 1
}

// ---------- 床に残る光の跡（②だけ）----------
/** i 本目の跡の明るさ（残った直後に強く、熾火の 0.35 へ。合図で灯り直し、大技の後に消える） */
export function battleTrail(i: number, t: number): number {
  const u = t % ROUND, at = [B.j1 + 60, B.j2 + 60][i]
  if (at == null || u < at) return 0
  const ember = 0.35 + 0.65 * (1 - smooth(ramp(u, at + 60, at + 360)))
  return Math.max(ember, smooth(ramp(u, B.tell, B.tell + 240))) * (1 - smooth(ramp(u, B.lunge + 300, B.lunge + 900)))
}
function drawTrail(ctx: Ctx, t: number, e: StageEnv): void {
  const { k, G } = e, u = t % ROUND, x0 = e.at(tipAt(B.j1))[0] / k
  const seg = (i: number): [number, number] => [x0 + 50 - 40 * i, x0 + 84 - 40 * i]
  const line = (a: number, b: number, wd: number, col: string, al: number) => {
    if (al <= 0.01) return
    ctx.globalAlpha = al; ctx.strokeStyle = col; ctx.lineWidth = wd * k; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.moveTo(a * k, G - 1 * k); ctx.lineTo(b * k, G - 1 * k); ctx.stroke()
  }
  // 合図で跡が灯り直し、輪の止まる所までつながる（大技の通り道）
  const ln = smooth(ramp(u, B.tell + 60, B.lock)) * (1 - smooth(ramp(u, B.lunge + 300, B.lunge + 900)))
  if (ln > 0.01) {
    const st = x0 + 84, end = st + (battleSpot(t).x - st) * ln
    line(end, st, 7, '#7fc4ff', 0.12 * ln); line(end, st, 2, '#bfe3ff', 0.55 * ln)
  }
  for (let i = 0; i < 2; i++) {
    const v = battleTrail(i, t)
    if (v <= 0.01) continue
    const [a, b] = seg(i)
    line(a, b, 9, '#7fc4ff', 0.10 * v); line(a, b, 3, '#7fc4ff', 0.6 * v); line(a + 4, b - 4, 1.2, '#ffffff', 0.8 * v)
  }
  ctx.globalAlpha = 1
}

// ---------- 2本 ----------
function phases(trailToo: boolean): MockupPhase[] {
  const out: MockupPhase[] = []
  for (const r of [0, 1]) {
    const o = r * ROUND, n = r ? '2周目' : '1周目'
    out.push(
      { from: o, to: o + 400, label: `${n}：構え（光の輪は先代を照らす）`, kind: 'rest' },
      { from: o + 400, to: o + 1000, label: `1回目の突きをパリィ・輪が1段狭まる${trailToo ? '・床に跡が1本' : ''}`, kind: 'hit' },
      { from: o + 1000, to: o + B.tell, label: `2回目の突きをパリィ・もう1段${trailToo ? '・跡が2本' : ''}`, kind: 'hit' },
      { from: o + B.tell, to: o + B.lock, label: `合図：輪が主人公へ移って追う${trailToo ? '・跡がつながって伸びる' : ''}`, kind: 'tell' },
      { from: o + B.lock, to: o + B.lunge, label: r ? '輪が止まって白く固まる → 跳んで輪から出る' : '輪が止まって白く固まる（輪の中に残る）', kind: 'tell' },
      { from: o + B.lunge, to: o + B.lunge + 400, label: r ? '大技は白い輪へ・空振り' : '大技は白い輪へ・当たる', kind: 'hit' },
      { from: o + B.lunge + 400, to: o + ROUND, label: r ? '空振りの戻りへ斬り返す' : '戻り（輪は先代へ戻る）', kind: 'rest' },
    )
  }
  return out
}
function make(id: string, label: string, trailToo: boolean): MockupMove {
  return {
    id, boss: '先代の姫', label, body: 'human-rapier-skirt', durMs: ROUND * 2, scale: 0.95, baseX: 300, heroAt: 150,
    keys: [], poseFn: t => {
      const u = t % ROUND
      let i = 0
      while (i < KEYS.length - 2 && KEYS[i + 1]!.at <= u) i++
      return KEYS[i]!.pose
    },
    phases: phases(trailToo), hits: [B.j1, B.j2, B.lunge, ROUND + B.j1, ROUND + B.j2, ROUND + B.lunge], activeMs: 60,
    box: [40, 120, -128, -88], paths: ['sF'],
    cues: [B.j1 + 120, B.j2 + 120, B.lock, ROUND + B.j1 + 120, ROUND + B.j2 + 120, ROUND + B.lock].map(at => ({ at, ms: 80 })),
    source: '拍は仮。合図から大技までを 800ms に延ばした（輪が追って止まる・出る間）。帯の黄色＝輪が狭まった瞬間と、輪が止まった瞬間。1周目は輪の中に残って受け、2周目は白い輪から跳んで出て斬り返す。',
    stage: { back: trailToo ? (c, t, e) => { drawSpot(c, t, e); drawTrail(c, t, e) } : drawSpot, front: drawSparks },
    hero: drawHeroFig,
  }
}
export const PRINCESS_BATTLE_SPOT_MOCKUP = make('princess-battle-spot', '戦いの場面 ① 追う照明だけ（輪が狭まって数を知らせ、合図で主人公を追って止まった所へ大技）', false)
export const PRINCESS_BATTLE_SPOT_TRAIL_MOCKUP = make('princess-battle-spot-trail', '戦いの場面 ② 追う照明＋床に残る光の跡（跡の数でも数を知らせ、合図で跡が輪までつながる）', true)
export const PRINCESS_BATTLE_MOCKUPS = [PRINCESS_BATTLE_SPOT_MOCKUP, PRINCESS_BATTLE_SPOT_TRAIL_MOCKUP]
